import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type {
  AsapWindowInput, AsapWindowPolicy, AsapWindowSetting, BillingRateInput, BillingRatePolicy, BillingRateRecorded,
  CourtReportAccess, VisitRate, VisitRateInput, VisitRatePolicies,
} from './types.ts';

/** 164: drafting roles that cannot read a section the court reports draw on, a configuration fault. Tier 1 only. */
export function useCourtReportAccess(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['policies', 'court-report-access'],
    queryFn: () => apiFetch<CourtReportAccess>('/policies/court-report-access', getToken),
    ...queryDefaults,
    enabled,
  });
}

/** 165: billing rates, per role and per person, in force, scheduled and on record. Tier 1 only. */
export function useBillingRates(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['policies', 'billing-rates'],
    queryFn: () => apiFetch<BillingRatePolicy>('/policies/billing-rates', getToken),
    ...queryDefaults,
    enabled,
  });
}

/** 165: record a billing rate. The server inserts through the database's one writer; nothing here changes a rate. */
export function useRecordBillingRate() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BillingRateInput) =>
      apiFetch<{ rate: BillingRateRecorded }>('/policies/billing-rates', getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['policies', 'billing-rates'] }); },
  });
}

/** Visit rates by residence type: current, scheduled and earlier. Tier 1 only. */
export function useVisitRates(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['policies', 'visit-rates'],
    queryFn: () => apiFetch<VisitRatePolicies>('/policies/visit-rates', getToken),
    ...queryDefaults,
    enabled,
  });
}

/**
 * Record a new visit rate. The server inserts; nothing here can change a rate
 * already on record. A rate in force today changes what clients owe, so the
 * client and dashboard queries are refreshed as well as the policies.
 */
export function useRecordVisitRate() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: VisitRateInput) =>
      apiFetch<{ residence_type_label: string; rate: VisitRate }>('/policies/visit-rates', getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      void queryClient.invalidateQueries({ queryKey: ['client'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}

/** 155: the ASAP window, in force, scheduled and earlier. Tier 1 only. */
export function useAsapWindow(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['policies', 'asap-window'],
    queryFn: () => apiFetch<AsapWindowPolicy>('/policies/asap-window', getToken),
    ...queryDefaults,
    enabled,
  });
}

/**
 * 155: record a new ASAP window. The server inserts through the database's one writer; nothing here can change a
 * setting on record. A setting in force today changes which tasks show ASAP, so the task queries are refreshed too.
 */
export function useRecordAsapWindow() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AsapWindowInput) =>
      apiFetch<{ setting: AsapWindowSetting }>('/policies/asap-window', getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}
